import React, { useState } from 'react';
import { Env, ErrorBanner, Spinner } from '../../shared';
import { FieldLabel, inputClass, selectClass } from '../../create/shared/FieldLabel';
import { BusinessMultiPicker, PickedBusiness } from '../../create/shared/BusinessPicker';
import { useCampaignMutations } from '../api/useReferralCampaigns';
import { useReferralCodes } from '../api/useReferralCodes';
import {
  CampaignCreateStatus,
  CampaignStatus,
  CreateCampaignBody,
  MAX_CLAIMS_MAX,
  MAX_PAGE_SIZE,
  PromoCampaign,
  UpdateCampaignBody,
} from '../api/types';
import { isoToLocalInput, localInputToIso } from '../format';
import { CampaignStatusBadge, CodeKindBadge, CodeStatusBadge, FallbackDealBadge } from './controls';

/**
 * The ambassador tag lives on `referral_codes.ambassador_ref`, not on the
 * campaign — a campaign is a deal, and one deal can carry several promoters'
 * codes, each with its own tag. So the builder shows the tags reaching this
 * campaign (read-only) and points at the code form, which is where a tag can
 * actually be set. Editing it here would 400: the campaign endpoints reject any
 * field outside their allow-list.
 */
function AmbassadorRefs({ env, campaignId }: { env: Env; campaignId: string }) {
  const { data, loading } = useReferralCodes(env, { campaign_id: campaignId, limit: MAX_PAGE_SIZE });
  const codes = data?.codes ?? [];

  return (
    <div className="border border-gray-800 rounded-xl p-3.5 bg-gray-900/40">
      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Ambassador refs</p>
      {loading ? (
        <p className="text-xs text-gray-500 mt-2">Loading attached codes…</p>
      ) : codes.length === 0 ? (
        <p className="text-xs text-gray-500 mt-2">
          No codes attach this campaign yet. A code's ambassador tag is set on the code itself.
        </p>
      ) : (
        <ul className="mt-2 space-y-1.5">
          {codes.map((c) => (
            <li key={c.code} className="flex items-center gap-2 flex-wrap text-xs">
              <span className="font-mono text-violet-300">{c.code}</span>
              <CodeKindBadge kind={c.kind} />
              {c.status === 'disabled' && <CodeStatusBadge status={c.status} />}
              <span className="text-gray-300">{c.ambassador_ref || <span className="text-gray-600">no tag</span>}</span>
              {c.owner && (
                <span className="text-gray-500 truncate">
                  · {c.owner.display_name || c.owner.email || c.owner.user_id}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
      <p className="text-xs text-gray-500 mt-2">
        Tags belong to the code, not the deal — edit one under <b>Codes &amp; dashboard</b>.
      </p>
    </div>
  );
}

const NAME_MAX = 120;
const DEAL_MAX = 500;
const TEMPLATE_MAX = 50;

/**
 * Deal-campaign builder: deal text, window, first-N cap, template hook and the
 * multi-select eligible-business set.
 *
 * Two things the form has to be honest about:
 *   * a new campaign starts as a `draft` — the consumer grants deals only from
 *     `active` campaigns inside their window, so going live is a deliberate
 *     second step;
 *   * `business_ids` REPLACES the eligible set on save (it is a multi-select, not
 *     an append), and an empty set means "redeemable anywhere", not "unset".
 *
 * Editing the `personal_default` row is allowed — it is the fallback deal every
 * un-attached code lands on — so it carries a blast-radius warning and the
 * backend's own `warnings` come back on save.
 */
export function CampaignForm({
  env,
  existing,
  onCancel,
  onSaved,
}: {
  env: Env;
  existing?: PromoCampaign;
  onCancel: () => void;
  onSaved: (campaign: PromoCampaign, message: string) => void;
}) {
  const isEdit = !!existing;
  const isFallback = existing?.kind === 'personal_default';

  const [name, setName] = useState(existing?.name ?? '');
  const [deal, setDeal] = useState(existing?.deal_description ?? '');
  const [startsAt, setStartsAt] = useState(isoToLocalInput(existing?.starts_at));
  const [endsAt, setEndsAt] = useState(isoToLocalInput(existing?.ends_at));
  const [maxClaims, setMaxClaims] = useState(
    existing?.max_claims != null ? String(existing.max_claims) : '',
  );
  const [templateKey, setTemplateKey] = useState(existing?.template_key ?? '');
  const [status, setStatus] = useState<CampaignStatus>(existing?.status ?? 'draft');
  const [businesses, setBusinesses] = useState<PickedBusiness[]>(
    (existing?.eligible_businesses ?? []).map((b) => ({
      id: b.business_id,
      name: b.name ?? b.business_id,
      timezone: null,
    })),
  );
  const [reason, setReason] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  const { create, update, saving, error } = useCampaignMutations(env);

  const parsedCap = maxClaims.trim() === '' ? null : Number(maxClaims.trim());
  const capInvalid =
    parsedCap !== null &&
    (!Number.isInteger(parsedCap) || parsedCap < 1 || parsedCap > MAX_CLAIMS_MAX);

  const startIso = localInputToIso(startsAt);
  const endIso = localInputToIso(endsAt);

  const validate = (): string | null => {
    if (!name.trim()) return 'Name is required.';
    if (name.trim().length > NAME_MAX) return `Name must be at most ${NAME_MAX} characters.`;
    if (!deal.trim()) return 'Deal text is required — it is what the referred user is shown.';
    if (deal.trim().length > DEAL_MAX) return `Deal text must be at most ${DEAL_MAX} characters.`;
    if (!isFallback) {
      if (!startIso || !endIso) return 'A start and end are both required.';
      if (new Date(endIso) <= new Date(startIso)) return 'The end must be after the start.';
    } else if (startIso && endIso && new Date(endIso) <= new Date(startIso)) {
      return 'The end must be after the start.';
    }
    if (capInvalid) return `The claim cap must be a whole number between 1 and ${MAX_CLAIMS_MAX.toLocaleString()}.`;
    if (templateKey.trim().length > TEMPLATE_MAX) {
      return `Template key must be at most ${TEMPLATE_MAX} characters.`;
    }
    if (parsedCap !== null && existing && parsedCap < existing.claim_count) {
      // Allowed by the backend and a legitimate way to freeze a campaign, so
      // this is a confirmation, not a block.
      return `Heads up: ${existing.claim_count} claims are already taken, so a cap of ${parsedCap} freezes the campaign. Save again to confirm.`;
    }
    return null;
  };

  const [capWarningAcked, setCapWarningAcked] = useState(false);

  const submitCreate = async () => {
    const body: CreateCampaignBody = {
      name: name.trim(),
      deal_description: deal.trim(),
      starts_at: startIso!,
      ends_at: endIso!,
      max_claims: parsedCap,
      business_ids: businesses.map((b) => b.id),
      ...(templateKey.trim() ? { template_key: templateKey.trim() } : {}),
      status: (status === 'ended' ? 'draft' : status) as CampaignCreateStatus,
      ...(reason.trim() ? { reason: reason.trim() } : {}),
    };
    const saved = await create(body);
    if (saved) onSaved(saved, `Created “${saved.name}” as ${saved.status}.`);
  };

  const submitEdit = async () => {
    if (!existing) return;
    const body: UpdateCampaignBody = {};
    if (name.trim() !== existing.name) body.name = name.trim();
    if (deal.trim() !== existing.deal_description) body.deal_description = deal.trim();
    if (startIso && startIso !== toIso(existing.starts_at)) body.starts_at = startIso;
    if (endIso && endIso !== toIso(existing.ends_at)) body.ends_at = endIso;
    if (parsedCap !== (existing.max_claims ?? null)) body.max_claims = parsedCap;
    if ((templateKey.trim() || null) !== (existing.template_key ?? null)) {
      body.template_key = templateKey.trim() || null;
    }
    if (status !== existing.status) body.status = status;

    const originalIds = (existing.eligible_businesses ?? []).map((b) => b.business_id);
    const nextIds = businesses.map((b) => b.id);
    if (originalIds.length !== nextIds.length || nextIds.some((id) => !originalIds.includes(id))) {
      body.business_ids = nextIds;
    }

    if (Object.keys(body).length === 0) {
      setFormError('Nothing changed.');
      return;
    }
    if (reason.trim()) body.reason = reason.trim();

    const saved = await update(existing.id, body);
    if (saved) {
      const warnings = saved.warnings?.length ? ` Warning: ${saved.warnings.join(' ')}` : '';
      onSaved(saved, `Saved “${saved.name}”.${warnings}`);
    }
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const problem = validate();
    const isCapWarning = !!problem?.startsWith('Heads up:');
    if (problem && !(isCapWarning && capWarningAcked)) {
      setFormError(problem);
      if (isCapWarning) setCapWarningAcked(true);
      return;
    }
    setFormError(null);
    if (isEdit) await submitEdit();
    else await submitCreate();
  };

  return (
    <form onSubmit={onSubmit} className="space-y-5 max-w-3xl">
      <div className="flex items-center gap-3 flex-wrap">
        <button
          type="button"
          onClick={onCancel}
          className="px-2.5 py-1.5 text-xs font-medium rounded-lg bg-gray-800 text-gray-200 hover:bg-gray-700"
        >
          ← Cancel
        </button>
        <h3 className="text-lg font-semibold text-white">
          {isEdit ? `Edit “${existing!.name}”` : 'New deal campaign'}
        </h3>
        {existing && <CampaignStatusBadge status={existing.status} />}
        {isFallback && <FallbackDealBadge />}
      </div>

      {isFallback && (
        <div className="px-3 py-2 bg-amber-950/30 border border-amber-900/50 rounded-lg text-xs text-amber-300">
          This is the <b>fallback deal</b>. Every promoter and campaign code without an explicit campaign
          grants it, so a bad edit here stops deal grants platform-wide.
        </div>
      )}

      <div>
        <FieldLabel required>Name</FieldLabel>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Spring 2026 bar crawl"
          maxLength={NAME_MAX}
          className={inputClass}
        />
      </div>

      <div>
        <FieldLabel required>Deal text</FieldLabel>
        <textarea
          value={deal}
          onChange={(e) => setDeal(e.target.value)}
          rows={3}
          maxLength={DEAL_MAX}
          placeholder="Free draft beer on your first check-in"
          className={inputClass}
        />
        <p className="text-xs text-gray-500 mt-1">
          Shown to the referred user. {deal.length}/{DEAL_MAX} characters.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <FieldLabel required={!isFallback}>Starts</FieldLabel>
          <input
            type="datetime-local"
            value={startsAt}
            onChange={(e) => setStartsAt(e.target.value)}
            className={inputClass}
          />
        </div>
        <div>
          <FieldLabel required={!isFallback}>Ends</FieldLabel>
          <input
            type="datetime-local"
            value={endsAt}
            onChange={(e) => setEndsAt(e.target.value)}
            className={inputClass}
          />
        </div>
      </div>
      <p className="text-xs text-gray-500 -mt-3">
        Entered in your local time and sent as an absolute instant. Deals are granted only inside this
        window.
      </p>

      <div>
        <FieldLabel>Claim cap (first N)</FieldLabel>
        <input
          value={maxClaims}
          onChange={(e) => { setMaxClaims(e.target.value); setCapWarningAcked(false); }}
          placeholder="Leave blank for uncapped"
          inputMode="numeric"
          className={inputClass}
        />
        <p className="text-xs text-gray-500 mt-1">
          {existing
            ? `${existing.claim_count.toLocaleString()} claimed so far${
                existing.max_claims != null ? ` of ${existing.max_claims.toLocaleString()}` : ' (uncapped)'
              }.`
            : 'Caps how many users can claim the deal in total.'}
        </p>
      </div>

      <BusinessMultiPicker
        env={env}
        value={businesses}
        onChange={setBusinesses}
        label="Eligible businesses"
        emptyHint="No venues selected — the deal is redeemable at any business."
      />

      {existing ? (
        <AmbassadorRefs env={env} campaignId={existing.id} />
      ) : (
        <div className="px-3 py-2 bg-gray-900 border border-gray-800 rounded-lg text-xs text-gray-400">
          <b>Ambassador refs</b> are set per code, not per campaign — attach this campaign to a code and tag
          it there. They will be listed here once codes point at it.
        </div>
      )}

      <div>
        <FieldLabel>Template key</FieldLabel>
        <input
          value={templateKey}
          onChange={(e) => setTemplateKey(e.target.value)}
          placeholder="Optional deal-template hook"
          maxLength={TEMPLATE_MAX}
          className={inputClass}
        />
      </div>

      <div>
        <FieldLabel required>Status</FieldLabel>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value as CampaignStatus)}
          className={selectClass}
        >
          <option value="draft">draft — staged, grants nothing</option>
          <option value="active">active — grants inside the window</option>
          {isEdit && <option value="ended">ended — no new grants</option>}
        </select>
        <p className="text-xs text-gray-500 mt-1">
          {status === 'ended'
            ? 'Ending stops new grants; deals already issued keep their own expiry and stay redeemable.'
            : 'Only active campaigns inside their window grant deals.'}
        </p>
      </div>

      <div>
        <FieldLabel>Reason (audit note)</FieldLabel>
        <input
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Why this campaign is being created / changed"
          className={inputClass}
        />
      </div>

      {formError && <ErrorBanner message={formError} />}
      {error && <ErrorBanner message={error} />}

      <div className="flex items-center gap-3">
        <button
          type="submit"
          disabled={saving}
          className="px-4 py-2 text-sm font-semibold rounded-lg bg-violet-600 text-white hover:bg-violet-500 disabled:opacity-40"
        >
          {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Create campaign'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-sm font-medium rounded-lg text-gray-400 hover:text-gray-200"
        >
          Cancel
        </button>
        {saving && <Spinner size="sm" />}
      </div>
    </form>
  );
}

/** Normalizes a backend timestamp for comparison against a freshly built ISO. */
function toIso(value: string | null | undefined): string | null {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}
